// frontend/packages/business-console/src/pages/RyzzeCast.jsx
//
// RyzzeCast: compose a post once and route it to your website and connected
// channels (content syndication, Phase 1a). This slice is the composer + queue:
// write a draft, choose where it should go, and see your recent posts.
// Publishing rolls out one channel at a time (website first) in a later slice,
// so non-website channels show as "Soon" here.
//
// The page self-gates on GET /v2/ryzzecast/status (the content_syndication
// tenant feature), matching the UpRyzze AI pattern: routed + listed for all,
// but a friendly "not available" state for tenants who don't have it.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import FacebookOutlinedIcon from '@mui/icons-material/FacebookOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

import {
  getRyzzecastStatus,
  listPosts,
  createPost,
  updatePost,
  publishPost,
  getFacebookStatus,
  getFacebookConnectUrl,
  disconnectFacebook,
  syncFacebookPosts,
  retonePost,
} from '../services/ryzzecastService';
import {
  useTokens,
  PageHeader,
  Surface,
  StatusPill,
} from '../components/primitives';

// Channel catalog. `live` = a publisher exists today. Website is first; the
// rest are visible so the client can see what is coming, but not selectable yet.
const CHANNELS = [
  { key: 'website', label: 'Website', icon: LanguageOutlinedIcon, live: true },
  {
    key: 'gbp',
    label: 'Google Business',
    icon: StorefrontOutlinedIcon,
    live: false,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    icon: FacebookOutlinedIcon,
    live: false,
  },
  {
    key: 'instagram',
    label: 'Instagram',
    icon: CameraAltOutlinedIcon,
    live: false,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    icon: BusinessOutlinedIcon,
    live: false,
  },
];

const STATUS_PILL = {
  draft: { kind: 'neutral', label: 'Draft' },
  approved: { kind: 'info', label: 'Approved' },
  publishing: { kind: 'warning', label: 'Publishing' },
  published: { kind: 'success', label: 'Published' },
  skipped: { kind: 'neutral', label: 'Skipped' },
};

const MAX_TEXT = 20000;

// Friendly message per publish failure reason (server returns ok:false + reason).
const PUBLISH_REASON_MSG = {
  website_not_selected: 'Turn on Website for this post first.',
  retone_failed: 'Could not prepare the website version. Try again.',
  no_config: 'Your website is not connected yet. Contact support.',
  promote_failed: 'Your website is not connected yet. Contact support.',
  collection_not_allowed: 'Could not publish to your site. Contact support.',
  bad_slug: 'Could not publish this post. Contact support.',
  write_failed: 'Could not publish to your site. Try again.',
};

// Is Website routed for this post?
function websiteSelected(routing) {
  return routing?.website?.decision === 'selected';
}

// Which channels a post is routed to (decision === 'selected').
function selectedChannels(routing) {
  if (!routing || typeof routing !== 'object') return [];
  return CHANNELS.filter((c) => routing[c.key]?.decision === 'selected').map(
    (c) => c.key
  );
}

export default function RyzzeCast() {
  const t = useTokens();

  const [gate, setGate] = useState({ loading: true, enabled: false });
  const [posts, setPosts] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);

  // Composer state.
  const [text, setText] = useState('');
  const [routing, setRouting] = useState({ website: true });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Facebook connect (harvest wave). Only shown when the fb_harvest feature is
  // on for this tenant (the /v2/fb API 404s otherwise).
  const [fb, setFb] = useState({ enabled: false, loading: true });
  const [fbBusy, setFbBusy] = useState(false);

  const loadFb = useCallback(async () => {
    const status = await getFacebookStatus();
    setFb({ loading: false, ...status });
  }, []);

  const loadPosts = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const items = await listPosts();
      setPosts(items);
    } catch (err) {
      setListError(err?.response?.data?.error || 'Could not load your posts.');
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      const status = await getRyzzecastStatus();
      if (!alive) return;
      setGate({
        loading: false,
        enabled: Boolean(status?.enabled),
        siteBaseUrl: status?.siteBaseUrl || '',
      });
      if (status?.enabled) {
        loadPosts();
        loadFb();
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadPosts, loadFb]);

  const onConnectFacebook = async () => {
    setFbBusy(true);
    try {
      const url = await getFacebookConnectUrl();
      if (url) window.location.href = url;
    } catch {
      setFbBusy(false);
    }
  };

  const onDisconnectFacebook = async () => {
    setFbBusy(true);
    try {
      await disconnectFacebook();
      await loadFb();
    } finally {
      setFbBusy(false);
    }
  };

  // Backfill: pull the Page's recent posts in as drafts. Idempotent
  // server-side, so clicking twice never duplicates.
  const [syncMsg, setSyncMsg] = useState(null);
  const onSyncFacebookPosts = async () => {
    setFbBusy(true);
    setSyncMsg(null);
    try {
      const res = await syncFacebookPosts();
      setSyncMsg(
        res?.imported
          ? `Imported ${res.imported} post${res.imported === 1 ? '' : 's'}.`
          : 'No new posts to import.'
      );
      loadPosts();
    } catch (err) {
      setSyncMsg(
        err?.response?.data?.error || 'Could not sync posts. Try again.'
      );
    } finally {
      setFbBusy(false);
    }
  };

  const toggleChannel = (key) => setRouting((r) => ({ ...r, [key]: !r[key] }));

  const buildRouting = () => {
    const out = {};
    for (const c of CHANNELS) {
      if (!c.live) continue; // only routable channels have a publisher today
      out[c.key] = {
        decision: routing[c.key] ? 'selected' : 'skipped',
        status: 'pending',
      };
    }
    return out;
  };

  const onSave = async () => {
    const clean = text.trim();
    if (!clean) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await createPost({ text: clean, routing: buildRouting() });
      setText('');
      setRouting({ website: true });
      setSaved(true);
      loadPosts();
    } catch (err) {
      setSaveError(
        err?.response?.data?.error || 'Could not save your post. Try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const onApprove = async (post) => {
    try {
      const updated = await updatePost(post._id, { status: 'approved' });
      setPosts((list) => list.map((p) => (p._id === post._id ? updated : p)));
    } catch {
      // Non-fatal: leave the row as-is; a reload will reconcile.
      loadPosts();
    }
  };

  // Per-post publish state: which row is publishing, and a per-row message.
  const [publishingId, setPublishingId] = useState(null);
  const [rowMsg, setRowMsg] = useState({});

  // Website preview: runs the retone (the SAME text a publish would use, and
  // the result is saved on the post, so publishing later reuses it - no double
  // model cost) and expands the website version under the row.
  const [previewingId, setPreviewingId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState({});
  const onPreviewWebsite = async (post) => {
    // Already generated: just toggle visibility.
    if (post.variants?.website?.text) {
      setPreviewOpen((o) => ({ ...o, [post._id]: !o[post._id] }));
      return;
    }
    setPreviewingId(post._id);
    setRowMsg((m) => ({ ...m, [post._id]: null }));
    try {
      const res = await retonePost(post._id, ['website']);
      if (res?.item) {
        setPosts((list) =>
          list.map((p) => (p._id === post._id ? res.item : p))
        );
        setPreviewOpen((o) => ({ ...o, [post._id]: true }));
      }
      if (res && res.ok === false) {
        setRowMsg((m) => ({
          ...m,
          [post._id]: {
            severity: 'error',
            text: 'Could not prepare the website version. Try again.',
          },
        }));
      }
    } catch (err) {
      setRowMsg((m) => ({
        ...m,
        [post._id]: {
          severity: 'error',
          text:
            err?.response?.data?.error ||
            'Could not prepare the website version. Try again.',
        },
      }));
    } finally {
      setPreviewingId(null);
    }
  };

  const onPublish = async (post) => {
    setPublishingId(post._id);
    setRowMsg((m) => ({ ...m, [post._id]: null }));
    try {
      const res = await publishPost(post._id);
      if (res?.ok) {
        if (res.item) {
          setPosts((list) =>
            list.map((p) => (p._id === post._id ? res.item : p))
          );
        }
        setRowMsg((m) => ({
          ...m,
          [post._id]: {
            severity: 'success',
            text: 'Published to your website.',
          },
        }));
      } else {
        setRowMsg((m) => ({
          ...m,
          [post._id]: {
            severity: 'warning',
            text:
              PUBLISH_REASON_MSG[res?.reason] ||
              'Could not publish this post. Try again.',
          },
        }));
      }
    } catch (err) {
      setRowMsg((m) => ({
        ...m,
        [post._id]: {
          severity: 'error',
          text:
            err?.response?.data?.error ||
            'Could not publish this post. Try again.',
        },
      }));
    } finally {
      setPublishingId(null);
    }
  };

  // ---- Gating states -------------------------------------------------------
  if (gate.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (!gate.enabled) {
    return (
      <Box>
        <PageHeader title="RyzzeCast" sx={{ mb: 2 }} />
        <Surface sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
            RyzzeCast is not turned on for your business yet.
          </Typography>
          <Typography sx={{ color: t.muted, fontSize: 14 }}>
            RyzzeCast lets you write a post once and send it to your website and
            connected channels. Contact us at support@upryzze.com to turn it on.
          </Typography>
        </Surface>
      </Box>
    );
  }

  // ---- Enabled -------------------------------------------------------------
  return (
    <Box>
      <PageHeader
        title="RyzzeCast"
        meta={
          <Typography sx={{ color: t.muted, fontSize: 13.5 }}>
            Write once, send everywhere. Compose a post and choose where it
            goes.
          </Typography>
        }
        sx={{ mb: 2 }}
      />

      {/* Facebook connect (only when the fb_harvest feature is on) */}
      {fb.enabled ? (
        <Surface
          sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}
        >
          <FacebookOutlinedIcon sx={{ color: t.blue }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {fb.connected ? (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  Facebook connected{fb.pageName ? `: ${fb.pageName}` : ''}
                </Typography>
                <Typography sx={{ color: t.muted, fontSize: 13 }}>
                  {syncMsg ||
                    'New posts on your Page will show up here automatically.'}
                </Typography>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  Connect your Facebook Page
                </Typography>
                <Typography sx={{ color: t.muted, fontSize: 13 }}>
                  Pull your Facebook posts into RyzzeCast automatically.
                </Typography>
              </>
            )}
          </Box>
          {fb.connected ? (
            <>
              <Button
                size="small"
                variant="contained"
                onClick={onSyncFacebookPosts}
                disabled={fbBusy}
                startIcon={
                  fbBusy ? <CircularProgress size={14} color="inherit" /> : null
                }
              >
                Sync recent posts
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={onDisconnectFacebook}
                disabled={fbBusy}
              >
                Disconnect
              </Button>
            </>
          ) : (
            <Button
              size="small"
              variant="contained"
              onClick={onConnectFacebook}
              disabled={fbBusy}
              startIcon={
                fbBusy ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <FacebookOutlinedIcon />
                )
              }
            >
              Connect Facebook
            </Button>
          )}
        </Surface>
      ) : null}

      {/* Composer */}
      <Surface sx={{ p: 2.5, mb: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="Write your post"
            placeholder="What do you want to share?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            inputProps={{ maxLength: MAX_TEXT }}
          />

          <Box>
            <Typography
              sx={{ fontSize: 12.5, fontWeight: 700, color: t.muted, mb: 1 }}
            >
              Send to
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const on = c.live && Boolean(routing[c.key]);
                return (
                  <Chip
                    key={c.key}
                    icon={<Icon fontSize="small" />}
                    label={c.live ? c.label : `${c.label} (soon)`}
                    onClick={c.live ? () => toggleChannel(c.key) : undefined}
                    disabled={!c.live}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                );
              })}
            </Stack>
            <Typography sx={{ color: t.faint, fontSize: 12, mt: 1 }}>
              Publishing rolls out one channel at a time. Website is first; the
              rest are coming soon.
            </Typography>
          </Box>

          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saved ? (
            <Alert severity="success">Saved to your queue as a draft.</Alert>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || !text.trim()}
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CampaignOutlinedIcon />
                )
              }
            >
              {saving ? 'Saving' : 'Save draft'}
            </Button>
          </Box>
        </Stack>
      </Surface>

      {/* Queue */}
      <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 1.25 }}>
        Your posts
      </Typography>

      {listLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : listError ? (
        <Alert severity="error">{listError}</Alert>
      ) : posts.length === 0 ? (
        <Surface sx={{ p: 3 }}>
          <Typography sx={{ color: t.muted }}>
            No posts yet. Compose your first one above.
          </Typography>
        </Surface>
      ) : (
        <Stack spacing={1.25}>
          {posts.map((post) => {
            const pill = STATUS_PILL[post.status] || STATUS_PILL.draft;
            const chans = selectedChannels(post.routing);
            return (
              <Surface key={post._id} sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        whiteSpace: 'pre-wrap',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {post.original?.text || ''}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      sx={{ mt: 1, flexWrap: 'wrap', gap: 0.75 }}
                    >
                      {chans.length === 0 ? (
                        <Typography sx={{ color: t.faint, fontSize: 12 }}>
                          No channels selected
                        </Typography>
                      ) : (
                        chans.map((key) => {
                          const c = CHANNELS.find((x) => x.key === key);
                          return (
                            <Chip
                              key={key}
                              size="small"
                              label={c ? c.label : key}
                              variant="outlined"
                            />
                          );
                        })
                      )}
                    </Stack>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <StatusPill kind={pill.kind} label={pill.label} />
                    {post.status === 'draft' ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onApprove(post)}
                      >
                        Approve
                      </Button>
                    ) : null}
                    {websiteSelected(post.routing) ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onPreviewWebsite(post)}
                        disabled={previewingId === post._id}
                        startIcon={
                          previewingId === post._id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null
                        }
                      >
                        {previewingId === post._id
                          ? 'Preparing'
                          : post.variants?.website?.text
                            ? previewOpen[post._id]
                              ? 'Hide website version'
                              : 'Show website version'
                            : 'Preview website version'}
                      </Button>
                    ) : null}
                    {post.routing?.website?.status === 'published' &&
                    post.routing?.website?.externalId &&
                    gate.siteBaseUrl ? (
                      <Button
                        size="small"
                        variant="text"
                        component="a"
                        href={`${gate.siteBaseUrl}/blog/${post.routing.website.externalId}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on site
                      </Button>
                    ) : null}
                    {websiteSelected(post.routing) &&
                    post.status !== 'published' ? (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onPublish(post)}
                        disabled={publishingId === post._id}
                        startIcon={
                          publishingId === post._id ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : null
                        }
                      >
                        {publishingId === post._id
                          ? 'Publishing'
                          : 'Publish to website'}
                      </Button>
                    ) : null}
                  </Stack>
                </Box>
                {previewOpen[post._id] && post.variants?.website?.text ? (
                  <Box
                    sx={{
                      mt: 1.25,
                      p: 1.5,
                      borderRadius: 1.5,
                      border: `1px solid ${t.cardBorder}`,
                      background: t.grayBg || 'transparent',
                    }}
                  >
                    <Typography
                      sx={{
                        color: t.muted,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        mb: 0.75,
                      }}
                    >
                      Website version
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                      {post.variants.website.text}
                    </Typography>
                  </Box>
                ) : null}
                {rowMsg[post._id] ? (
                  <Alert
                    severity={rowMsg[post._id].severity}
                    sx={{ mt: 1.25 }}
                    onClose={() =>
                      setRowMsg((m) => ({ ...m, [post._id]: null }))
                    }
                  >
                    {rowMsg[post._id].text}
                  </Alert>
                ) : null}
              </Surface>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
